/**
 * Date utility functions for consistent date formatting across the app
 */

/**
 * Format a date string for display without timezone shift.
 * Takes "2025-11-25T00:00:00.000Z" and displays as "11/25/2025" regardless of local timezone.
 */
export const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  // Extract just the date part (YYYY-MM-DD) to avoid timezone conversion
  const datePart = dateString.split('T')[0];
  const [year, month, day] = datePart.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString();
};

/**
 * Format a date as compact mm/dd/yy for tight UI spaces (e.g. mobile tables).
 */
export const formatDateCompact = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  const datePart = dateString.split('T')[0];
  const [year, month, day] = datePart.split('-').map(Number);
  const yy = String(year).slice(-2);
  return `${month}/${day}/${yy}`;
};

/**
 * Format a date string with time for display.
 * Uses the browser's locale settings.
 */
export const formatDateTime = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleString();
};

/**
 * Format a date string with short month and time (e.g., "Dec 5, 2:30 PM")
 */
export const formatShortDateTime = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

/**
 * Format a date string with short month, year, and time (e.g., "Dec 5, 2025, 2:30 PM")
 */
export const formatDateTimeShort = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

/**
 * Get relative time string (e.g., "2 hours ago", "yesterday")
 */
export const formatRelativeTime = (dateString: string | null | undefined): string => {
  if (!dateString) return '';

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;

  return formatDate(dateString);
};

/**
 * Format a date string with weekday, short month, and day (e.g., "Mon, Dec 5")
 */
export const formatDateWithWeekday = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Format a date string with weekday, short month, day, and year (e.g., "Mon, Dec 5, 2025")
 */
export const formatDateWithWeekdayAndYear = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * Format a date string as short date (e.g., "Dec 5, 2025")
 */
export const formatDateMedium = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * Format a date string as short date without year (e.g., "Dec 5")
 */
export const formatDateShortNoYear = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Parse a date-ONLY string ("2026-08-05", or the date part of an ISO
 * timestamp) as LOCAL midnight rather than UTC midnight. `new Date('2026-08-05')`
 * parses as UTC midnight, which for any timezone behind UTC is the previous
 * local calendar day -- use this for genuinely date-only fields (an
 * `<input type="date">` value, a due date with no time component), never
 * for real timestamps that should keep their UTC instant.
 */
export const parseDateOnly = (dateString: string): Date => {
  const datePart = dateString.split('T')[0];
  const [year, month, day] = datePart.split('-').map(Number);
  return new Date(year, month - 1, day);
};

/**
 * Inverse of `parseDateOnly`: format a local `Date` (as constructed by
 * `parseDateOnly`, or a date-picker's local-midnight selection) back to
 * "YYYY-MM-DD" using local getters. Never use `toISOString()` for this --
 * it reads UTC fields and reintroduces the same shift `parseDateOnly` exists
 * to avoid.
 */
export const formatDateOnlyFromDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Format a date-ONLY string ("2026-08-05") as "Aug 5" without the
 * UTC-midnight timezone shift `formatDateShortNoYear` has (see
 * `parseDateOnly`'s doc comment). Use for date-only fields; use
 * `formatDateShortNoYear` for real timestamps.
 */
export const formatDateOnlyShortNoYear = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  return parseDateOnly(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Format a relative time in compact form for widgets (e.g., "now", "5m", "2h", "3d")
 */
export const formatRelativeTimeCompact = (dateString: string | null | undefined): string => {
  if (!dateString) return '';

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'now';
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return date.toLocaleDateString();
};

/**
 * Convert a date string to YYYY-MM-DD format for input fields
 */
export const toInputDateFormat = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  return dateString.split('T')[0];
};

// ============================================================================
// Daylight Saving Time (DST) Utilities
// ============================================================================

/**
 * Check if a timezone observes DST by testing if offset changes between Jan and Jul
 */
export const timezoneObservesDst = (timezone: string): boolean => {
  try {
    const jan = new Date(new Date().getFullYear(), 0, 1);
    const jul = new Date(new Date().getFullYear(), 6, 1);

    const janOffset = getTimezoneOffsetMinutes(jan, timezone);
    const julOffset = getTimezoneOffsetMinutes(jul, timezone);

    return janOffset !== julOffset;
  } catch {
    return false;
  }
};

/**
 * Get the current UTC offset in minutes for a timezone
 */
export const getTimezoneOffsetMinutes = (date: Date, timezone: string): number => {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'shortOffset',
    });
    const parts = formatter.formatToParts(date);
    const offsetPart = parts.find(p => p.type === 'timeZoneName')?.value || '';

    // Parse offset like "GMT-5" or "GMT+5:30"
    const offsetMatch = offsetPart.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
    if (offsetMatch) {
      const sign = offsetMatch[1] === '+' ? 1 : -1;
      const hours = parseInt(offsetMatch[2], 10);
      const minutes = parseInt(offsetMatch[3] || '0', 10);
      return sign * (hours * 60 + minutes);
    }

    // Fallback: calculate offset by comparing dates
    const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
    const tzDate = new Date(date.toLocaleString('en-US', { timeZone: timezone }));
    return Math.round((tzDate.getTime() - utcDate.getTime()) / 60000);
  } catch {
    return 0;
  }
};

/**
 * Format UTC offset in minutes to human-readable string (e.g., "UTC-5" or "UTC+5:30")
 */
export const formatUtcOffset = (offsetMinutes: number): string => {
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absOffset = Math.abs(offsetMinutes);
  const hours = Math.floor(absOffset / 60);
  const minutes = absOffset % 60;

  if (minutes === 0) {
    return `UTC${sign}${hours}`;
  }
  return `UTC${sign}${hours}:${minutes.toString().padStart(2, '0')}`;
};

/**
 * Get information about DST transitions for a timezone
 */
export interface DstTransitionInfo {
  observesDst: boolean;
  currentOffset: number;
  currentOffsetFormatted: string;
  isDstActive: boolean;
  nextTransition: Date | null;
  nextTransitionType: 'spring_forward' | 'fall_back' | null;
  standardOffset: number;
  dstOffset: number;
}

export const getDstInfo = (timezone: string): DstTransitionInfo => {
  const now = new Date();
  const observesDst = timezoneObservesDst(timezone);
  const currentOffset = getTimezoneOffsetMinutes(now, timezone);

  if (!observesDst) {
    return {
      observesDst: false,
      currentOffset,
      currentOffsetFormatted: formatUtcOffset(currentOffset),
      isDstActive: false,
      nextTransition: null,
      nextTransitionType: null,
      standardOffset: currentOffset,
      dstOffset: currentOffset,
    };
  }

  // Find standard and DST offsets
  const jan = new Date(now.getFullYear(), 0, 1);
  const jul = new Date(now.getFullYear(), 6, 1);
  const janOffset = getTimezoneOffsetMinutes(jan, timezone);
  const julOffset = getTimezoneOffsetMinutes(jul, timezone);

  // In Northern Hemisphere, DST offset is larger (less negative or more positive)
  // In Southern Hemisphere, it's opposite
  const standardOffset = Math.min(janOffset, julOffset);
  const dstOffset = Math.max(janOffset, julOffset);
  const isDstActive = currentOffset === dstOffset;

  // Find next DST transition by binary search
  const nextTransition = findNextDstTransition(now, timezone);
  const nextTransitionType = nextTransition
    ? isDstActive ? 'fall_back' : 'spring_forward'
    : null;

  return {
    observesDst: true,
    currentOffset,
    currentOffsetFormatted: formatUtcOffset(currentOffset),
    isDstActive,
    nextTransition,
    nextTransitionType,
    standardOffset,
    dstOffset,
  };
};

/**
 * Find the next DST transition date (approximate, within a few hours)
 */
const findNextDstTransition = (from: Date, timezone: string): Date | null => {
  const currentOffset = getTimezoneOffsetMinutes(from, timezone);
  const searchEndDate = new Date(from);
  searchEndDate.setFullYear(searchEndDate.getFullYear() + 1);

  // Search in weekly increments first
  let searchDate = new Date(from);
  while (searchDate < searchEndDate) {
    searchDate.setDate(searchDate.getDate() + 7);
    const newOffset = getTimezoneOffsetMinutes(searchDate, timezone);
    if (newOffset !== currentOffset) {
      // Found a week where transition occurred, narrow down
      searchDate.setDate(searchDate.getDate() - 7);
      break;
    }
  }

  if (searchDate >= searchEndDate) {
    return null; // No transition found in next year
  }

  // Narrow down to the day
  for (let i = 0; i < 7; i++) {
    searchDate.setDate(searchDate.getDate() + 1);
    const newOffset = getTimezoneOffsetMinutes(searchDate, timezone);
    if (newOffset !== currentOffset) {
      return searchDate;
    }
  }

  return null;
};

/**
 * Format a DST transition date for display
 */
export const formatDstTransition = (date: Date | null, type: 'spring_forward' | 'fall_back' | null): string => {
  if (!date || !type) return '';

  const dateStr = date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  const action = type === 'spring_forward' ? 'Clocks spring forward' : 'Clocks fall back';
  return `${action} on ${dateStr}`;
};

// ============================================================================
// Timezone-Aware Date Formatting
// ============================================================================

/**
 * Format a date string in a specific timezone.
 * Takes "2025-11-25" or "2025-11-25T00:00:00.000Z" and displays in the given timezone.
 * If no timezone provided, falls back to browser locale.
 */
export const formatDateInTimezone = (
  dateString: string | null | undefined,
  timezone?: string | null
): string => {
  if (!dateString) return '';

  // Extract just the date part (YYYY-MM-DD) to avoid timezone shifts
  const datePart = dateString.split('T')[0];
  const [year, month, day] = datePart.split('-').map(Number);

  // Create a date object at noon to avoid DST edge cases
  const date = new Date(year, month - 1, day, 12, 0, 0);

  if (timezone) {
    try {
      return date.toLocaleDateString('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
      });
    } catch {
      // Invalid timezone, fall back to locale
    }
  }

  return date.toLocaleDateString();
};

/**
 * Format a date range in a specific timezone.
 * Returns "Dec 25 - Dec 31, 2025" or similar format.
 */
export const formatDateRangeInTimezone = (
  startDate: string | null | undefined,
  endDate: string | null | undefined,
  timezone?: string | null
): string => {
  if (!startDate || !endDate) return '';

  const startPart = startDate.split('T')[0];
  const endPart = endDate.split('T')[0];
  const [startYear, startMonth, startDay] = startPart.split('-').map(Number);
  const [endYear, endMonth, endDay] = endPart.split('-').map(Number);

  const start = new Date(startYear, startMonth - 1, startDay, 12, 0, 0);
  const end = new Date(endYear, endMonth - 1, endDay, 12, 0, 0);

  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
  };

  if (timezone) {
    try {
      const startFormatted = start.toLocaleDateString('en-US', { ...options, timeZone: timezone });
      const endOptions = { ...options, year: 'numeric' as const, timeZone: timezone };
      const endFormatted = end.toLocaleDateString('en-US', endOptions);
      return `${startFormatted} - ${endFormatted}`;
    } catch {
      // Invalid timezone, fall back
    }
  }

  const startFormatted = start.toLocaleDateString('en-US', options);
  const endFormatted = end.toLocaleDateString('en-US', { ...options, year: 'numeric' });
  return `${startFormatted} - ${endFormatted}`;
};
